import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectNotificationHistory,
  selectUnreadNotificationCount,
  markAllNotificationsRead,
  dismissMailNotification,
  setSelectedThread,
} from "../../../Slices/mailUiSlice";
import { selectFolderAndLoad } from "../../../Slices/Thunk/graphMailThunks";

const NotificationBell = () => {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef    = useRef(null);

  // Legacy mail count (old system)
  const mails          = useSelector(state => state.EmailData?.requestMail);
  const legacyCount    = mails?.length || 0;

  // New Outlook notification history
  const notifHistory   = useSelector(selectNotificationHistory);
  const outlookUnread  = useSelector(selectUnreadNotificationCount);

  const totalCount = legacyCount + outlookUnread;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  function handleToggle() {
    const next = !open;
    setOpen(next);
    if (next && outlookUnread > 0) {
      dispatch(markAllNotificationsRead());
    }
  }

  function handleOutlookNotifClick(entry) {
    if (entry.folderId) {
      dispatch(selectFolderAndLoad({ folderId: entry.folderId }));
    }
    dispatch(setSelectedThread({ conversationId: entry.conversationId }));
    navigate("/personalOutlook");
    setOpen(false);
  }

  function handleLegacyMailClick(mail) {
    if (mail?.mail) {
      navigate("/personalOutlook");
    } else {
      navigate("/Outlook");
    }
    setOpen(false);
  }

  function shortText(text, n = 28) {
    if (!text) return "";
    return text.length > n ? text.slice(0, n) + "…" : text;
  }

  function formatTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffMins = Math.floor((now - d) / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Envelope icon */}
      <i
        className={totalCount ? "fa-solid fa-envelope fa-shake" : "fa-solid fa-envelope"}
        onClick={handleToggle}
        style={{ fontSize: 22, cursor: "pointer", color: "white" }}
      />

      {/* Badge */}
      {totalCount > 0 && (
        <span style={{
          position: "absolute", top: -5, right: -7,
          padding: "0 4px", background: "red", color: "white",
          fontSize: 10, borderRadius: 10, minWidth: 14,
          textAlign: "center", fontWeight: "bold", lineHeight: "16px",
          height: 16, display: "inline-block",
        }}>
          {totalCount > 99 ? "99+" : totalCount}
        </span>
      )}

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute", right: 0, top: 36,
          width: 320, maxHeight: 440,
          background: "#fff", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          zIndex: 9999, overflow: "hidden",
          border: "1px solid #e1dfdd",
          display: "flex", flexDirection: "column",
          fontFamily: "'Segoe UI', Tahoma, sans-serif",
        }}>
          {/* Header */}
          <div style={{
            padding: "10px 14px 8px",
            borderBottom: "1px solid #f0f0f0",
            background: "#faf9f8",
            fontSize: 13, fontWeight: 600, color: "#323130",
            flexShrink: 0,
          }}>
            Notifications {totalCount > 0 && <span style={{ color: "#8a8886", fontWeight: 400 }}>({totalCount})</span>}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>

            {/* ── Outlook mail notifications ── */}
            {notifHistory.length > 0 && (
              <>
                <div style={{
                  padding: "6px 14px 4px",
                  fontSize: 10, fontWeight: 700,
                  color: "#8a8886", letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  background: "#f8f8f8",
                }}>
                  Mail
                </div>
                {notifHistory.slice(0, 15).map((entry) => (
                  <div
                    key={entry.id}
                    onClick={() => handleOutlookNotifClick(entry)}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      padding: "9px 14px", cursor: "pointer",
                      borderBottom: "1px solid #f3f2f1",
                      background: entry.isRead ? "transparent" : "#f0f6ff",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#f3f2f1"; }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = entry.isRead ? "transparent" : "#f0f6ff";
                    }}
                  >
                    {/* Unread dot */}
                    <div style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: entry.isRead ? "transparent" : "#0f6cbd",
                      marginTop: 5, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "baseline", gap: 6,
                      }}>
                        <span style={{
                          fontSize: 12, fontWeight: entry.isRead ? 400 : 600,
                          color: "#323130",
                          overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap", flex: 1,
                        }}>
                          {entry.sender}
                        </span>
                        <span style={{ fontSize: 10, color: "#8a8886", flexShrink: 0 }}>
                          {formatTime(entry.receivedAt)}
                        </span>
                      </div>
                      <div style={{
                        fontSize: 11, color: "#605e5c",
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap", marginTop: 1,
                      }}>
                        {entry.type === "reply" ? "↩ " : ""}{shortText(entry.subject)}
                      </div>
                    </div>
                    {/* Dismiss button */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        dispatch(dismissMailNotification(entry.id));
                      }}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#a19f9d", fontSize: 10, padding: "2px 2px",
                        flexShrink: 0, lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </>
            )}

            {/* ── Legacy mail items ── */}
            {legacyCount > 0 && (
              <>
                <div style={{
                  padding: "6px 14px 4px",
                  fontSize: 10, fontWeight: 700,
                  color: "#8a8886", letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  background: "#f8f8f8",
                }}>
                  Inbox
                </div>
                {mails.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleLegacyMailClick(item)}
                    style={{
                      display: "flex", justifyContent: "space-between",
                      alignItems: "center",
                      padding: "9px 14px", cursor: "pointer",
                      borderBottom: "1px solid #f3f2f1", fontSize: 12, color: "#323130",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#f3f2f1"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <span>{shortText(item.mail, 22)}</span>
                    <div style={{
                      background: "#107c10", borderRadius: "50%",
                      width: 18, height: 18, textAlign: "center",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ fontSize: 10, color: "white" }}>{item.index}</span>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Empty state */}
            {totalCount === 0 && (
              <div style={{
                padding: "28px 16px", textAlign: "center",
                color: "#8a8886", fontSize: 13,
              }}>
                No new notifications
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
